import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _get from "lodash/get";
import _values from "lodash/values";

var _templateObject;

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { gql } from '@nerdwallet/apollo';
import classes from '@nerdwallet/base-styles/classes';
import { colors } from '@nerdwallet/base-styles/variables';
import { HelpBubble, StarRating, Type } from '@nerdwallet/currency';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import MarketplaceReviewLink, { GraphQlMarketplaceReviewLink, LINK_TYPES } from '@nerdwallet/react-marketplace-review-link';
import Star from './_star';
import { getTooltipText } from './lib/tooltips';
import styles from './marketplace-star-rating.module.less';
var RATING_TEXT = 'NerdWallet rating';
var NO_RATING_TEXT = 'Not yet rated';
export var LAYOUTS = {
  INLINE: 'inline',
  STACKED: 'stacked',
  BIG_NUMBER: 'big-number',
  NUMERIC: 'numeric',

  /**
   * @deprecated - Use small_numeric_inline with `hideRatingText`.
   */
  SMALL_NUMERIC: 'small_numeric',
  SMALL_NUMERIC_INLINE: 'small_numeric_inline'
};

var BaseMarketplaceStarRating = function BaseMarketplaceStarRating(_ref) {
  var _classNames4;

  var analytics = _ref.analytics,
      enableAnalytics = _ref.enableAnalytics,
      starRating = _ref.starRating,
      hideNumber = _ref.hideNumber,
      hideRatingText = _ref.hideRatingText,
      ratingTextHref = _ref.ratingTextHref,
      omitRatingTextHref = _ref.omitRatingTextHref,
      omitRatingNumberHref = _ref.omitRatingNumberHref,
      ratingTextTooltip = _ref.ratingTextTooltip,
      tooltipPlacement = _ref.tooltipPlacement,
      trkLocation = _ref.trkLocation,
      trkPageType = _ref.trkPageType,
      trkTaxonomy = _ref.trkTaxonomy,
      layout = _ref.layout,
      marketplaceEntity = _ref.marketplaceEntity,
      gqlMarketplaceEntity = _ref.gqlMarketplaceEntity,
      className = _ref.className,
      productType = _ref.productType,
      shouldDisplayInline = _ref.shouldDisplayInline,
      showNotYetRated = _ref.showNotYetRated,
      smallRatingText = _ref.smallRatingText;
  useDeprecatedPropsWarning({
    component: 'BaseMarketplaceStarRating',
    deprecatedProps: [{
      version: '2.11.0',
      deprecated: 'enableAnalytics',
      migration: 'use analytics instead'
    }],
    props: {
      enableAnalytics: enableAnalytics
    }
  });
  var isLayoutStacked = layout === LAYOUTS.STACKED;
  var isLayoutBigNumber = layout === LAYOUTS.BIG_NUMBER;
  var isLayoutNumeric = layout === LAYOUTS.NUMERIC;
  var isLayoutSmallNumeric = layout === LAYOUTS.SMALL_NUMERIC || layout === LAYOUTS.SMALL_NUMERIC_INLINE;
  var ReviewLinkComponent = gqlMarketplaceEntity ? GraphQlMarketplaceReviewLink : MarketplaceReviewLink;
  var entity = gqlMarketplaceEntity || marketplaceEntity;

  var _getTooltipText = getTooltipText(productType, ratingTextTooltip, entity),
      tooltipText = _getTooltipText.tooltipText,
      titleOverride = _getTooltipText.titleOverride;

  var starComponent = function starComponent(shouldShowNotYetRated) {
    var color = shouldShowNotYetRated ? colors['neutral-lighter'] : undefined;
    return /*#__PURE__*/React.createElement(Star, {
      className: classNames(_defineProperty({}, styles.noRatingStar, shouldShowNotYetRated)),
      firstHalfStarFill: color,
      secondHalfStarFill: color
    });
  };

  var starRatingComponent = function starRatingComponent() {
    var _ref2;

    return /*#__PURE__*/React.createElement(StarRating, {
      nerdwallet: true,
      rating: starRating,
      className: classNames([styles.stars, (_ref2 = {}, _defineProperty(_ref2, styles.layoutStacked, isLayoutStacked), _defineProperty(_ref2, styles.layoutBigNumber, isLayoutBigNumber), _ref2)])
    });
  };

  var maxAnnotationComponent = function maxAnnotationComponent() {
    return /*#__PURE__*/React.createElement(Type, {
      size: 1,
      inline: true,
      light: true,
      className: classNames([styles.ratingNumberMaxAnnotation, classes(['margin-left-1'])])
    }, "/5");
  };

  var ratingNumberComponent = function ratingNumberComponent() {
    var _ref3;

    return /*#__PURE__*/React.createElement(Type, {
      className: classNames([styles.ratingNumber, (_ref3 = {}, _defineProperty(_ref3, styles.layoutStacked, isLayoutStacked), _defineProperty(_ref3, styles.layoutBigNumber, isLayoutBigNumber), _defineProperty(_ref3, styles.accessibleLinkTarget, !omitRatingNumberHref && (isLayoutNumeric || isLayoutSmallNumeric)), _defineProperty(_ref3, styles.layoutSmallNumeric, isLayoutSmallNumeric), _ref3)]),
      component: "p"
    }, starRating.toFixed(1));
  };

  var ratingTextComponent = function ratingTextComponent() {
    var _ref4;

    return /*#__PURE__*/React.createElement("span", {
      className: classNames([styles.ratingText, (_ref4 = {}, _defineProperty(_ref4, styles.layoutBigNumber, isLayoutBigNumber), _defineProperty(_ref4, styles.layoutSmallNumeric, isLayoutSmallNumeric), _defineProperty(_ref4, styles.smallRatingText, smallRatingText), _ref4)])
    }, !omitRatingTextHref && ratingTextHref ? /*#__PURE__*/React.createElement(ReviewLinkComponent, {
      href: ratingTextHref,
      marketplaceEntity: entity,
      type: LINK_TYPES.LINK,
      trkLocation: trkLocation,
      trkPageType: trkPageType,
      trkTaxonomy: trkTaxonomy
    }, RATING_TEXT) : RATING_TEXT, tooltipText && /*#__PURE__*/React.createElement(Fragment, null, "\xA0", /*#__PURE__*/React.createElement(HelpBubble, {
      placement: tooltipPlacement,
      content: /*#__PURE__*/React.createElement("div", {
        className: styles.helpBubble
      }, tooltipText),
      title: titleOverride || 'How is this rating determined?',
      analytics: (enableAnalytics || analytics) && _objectSpread({
        entityNamePrefix: 'rating-link-tooltip'
      }, analytics)
    })));
  };

  var renderBigNumberLayout = function renderBigNumberLayout() {
    return /*#__PURE__*/React.createElement("div", {
      className: styles.flexContainer
    }, ratingNumberComponent(), /*#__PURE__*/React.createElement("div", null, ratingTextComponent(), starRatingComponent()));
  };

  var getRatingAriaLabel = function getRatingAriaLabel() {
    return starRating ? "rated ".concat(starRating.toFixed(1), " out of 5") : 'unrated';
  };

  var renderNumericLayout = function renderNumericLayout() {
    if (!starRating && !showNotYetRated) {
      return null;
    }

    var ratingAriaLabel = getRatingAriaLabel();
    var shouldShowNotYetRated = !starRating && showNotYetRated;
    var numericLayoutContent = /*#__PURE__*/React.createElement(Fragment, null, starComponent(shouldShowNotYetRated), shouldShowNotYetRated ? /*#__PURE__*/React.createElement(Type, {
      className: styles.noRatingText,
      light: true,
      size: 1
    }, NO_RATING_TEXT) : /*#__PURE__*/React.createElement("span", {
      "aria-label": ratingAriaLabel
    }, ratingNumberComponent(), maxAnnotationComponent()));
    return /*#__PURE__*/React.createElement(Fragment, null, !hideRatingText && ratingTextComponent(), !omitRatingNumberHref && !shouldShowNotYetRated && ratingTextHref ? /*#__PURE__*/React.createElement(ReviewLinkComponent, {
      "aria-label": "Nerdwallet product review; ".concat(ratingAriaLabel),
      className: styles.accessibleLink,
      href: ratingTextHref,
      marketplaceEntity: entity,
      type: LINK_TYPES.LINK,
      trkLocation: trkLocation,
      trkPageType: trkPageType,
      trkTaxonomy: trkTaxonomy
    }, numericLayoutContent) : /*#__PURE__*/React.createElement("span", {
      className: styles.noLinkLayout
    }, numericLayoutContent));
  };

  var renderSmallNumericLayout = function renderSmallNumericLayout() {
    var inline = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

    if (!starRating && !showNotYetRated) {
      return null;
    }

    var shouldShowNotYetRated = !starRating && showNotYetRated;
    var numericLayoutContent = /*#__PURE__*/React.createElement(Fragment, null, starComponent(shouldShowNotYetRated), shouldShowNotYetRated ? /*#__PURE__*/React.createElement(Type, {
      className: styles.noRatingText,
      light: true,
      size: 1
    }, NO_RATING_TEXT) : /*#__PURE__*/React.createElement("span", {
      "aria-label": getRatingAriaLabel()
    }, ratingNumberComponent(), maxAnnotationComponent()));
    return /*#__PURE__*/React.createElement(Fragment, null, !shouldShowNotYetRated && ratingTextHref && !omitRatingNumberHref ? /*#__PURE__*/React.createElement(ReviewLinkComponent, {
      className: classNames(styles.smallNumericLayoutLink, _defineProperty({}, styles.inline, inline)),
      href: ratingTextHref,
      marketplaceEntity: entity,
      type: LINK_TYPES.LINK,
      trkLocation: trkLocation,
      trkPageType: trkPageType,
      trkTaxonomy: trkTaxonomy
    }, numericLayoutContent) : /*#__PURE__*/React.createElement("span", {
      className: classNames(styles.smallNumericNoLinkLayout, _defineProperty({}, styles.inline, inline))
    }, numericLayoutContent), !hideRatingText && !shouldShowNotYetRated && inline && /*#__PURE__*/React.createElement(Fragment, null, "\xA0", ratingTextComponent()));
  };

  var renderContent = function renderContent() {
    if (!starRating && layout !== LAYOUTS.NUMERIC && layout !== LAYOUTS.SMALL_NUMERIC && layout !== LAYOUTS.SMALL_NUMERIC_INLINE) {
      if (showNotYetRated) {
        return /*#__PURE__*/React.createElement(Fragment, null, starRatingComponent(), /*#__PURE__*/React.createElement(Type, {
          size: 1,
          className: styles.noRatingText
        }, NO_RATING_TEXT));
      }

      return null;
    }

    switch (layout) {
      case LAYOUTS.BIG_NUMBER:
        return renderBigNumberLayout();

      case LAYOUTS.SMALL_NUMERIC:
        return renderSmallNumericLayout();

      case LAYOUTS.SMALL_NUMERIC_INLINE:
        return renderSmallNumericLayout(true);

      case LAYOUTS.NUMERIC:
        return renderNumericLayout();

      default:
        return /*#__PURE__*/React.createElement(Fragment, null, starRatingComponent(), !hideNumber && ratingNumberComponent(), ratingTextComponent());
    }
  };

  return /*#__PURE__*/React.createElement("div", {
    className: classNames(className, (_classNames4 = {}, _defineProperty(_classNames4, styles.displayInline, shouldDisplayInline), _defineProperty(_classNames4, styles.layoutNumeric, isLayoutNumeric), _defineProperty(_classNames4, styles.layoutSmallNumeric, isLayoutSmallNumeric), _classNames4))
  }, renderContent());
};

BaseMarketplaceStarRating.propTypes = {
  /** Optional classes to apply to the outer container. */
  className: PropTypes.string,
  analytics: PropTypes.shape({
    entityName: PropTypes.string
  }),

  /** (Deprecated) A flag enabling analytics */
  enableAnalytics: PropTypes.bool,

  /** Specify alternate layouts from the enum list */
  layout: PropTypes.oneOf(_values(LAYOUTS)),

  /** Href for the rating link. */
  ratingTextHref: PropTypes.string,

  /** If true, the link to the review on the rating text will be omitted. */
  omitRatingTextHref: PropTypes.bool,

  /** If true, the link to the review on the rating number will be omitted. */
  omitRatingNumberHref: PropTypes.bool,

  /** Tooltip for the rating text. */
  ratingTextTooltip: PropTypes.string,

  /** Help bubble tooltip placement */
  tooltipPlacement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),

  /** Displays rating text and star rating inline */
  shouldDisplayInline: PropTypes.bool,

  /** Shows not yet rated component with empty star rating */
  showNotYetRated: PropTypes.bool,

  /** Gives both rating text and help bubble a font size of size-1 instead of the default size-2 */
  smallRatingText: PropTypes.bool,

  /** Star rating value. */
  starRating: PropTypes.number,

  /** Hides the numerical rating. */
  hideNumber: PropTypes.bool,

  /** Hides the rating text. */
  hideRatingText: PropTypes.bool,

  /**  Marketplace entity object from marketplace, passed down to review link */
  marketplaceEntity: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /**  Marketplace entity object from graphQl, passed down to review link */
  gqlMarketplaceEntity: PropTypes.shape({
    reviewLink: PropTypes.string,
    starRating: PropTypes.number,
    starRatingTooltip: PropTypes.string
  }),

  /** Tracking location (required if ratingTextHref is specified). */
  trkLocation: PropTypes.string,

  /** Tracking page type (required if ratingTextHref is specified). */
  trkPageType: PropTypes.string,

  /** Tracking taxonomy (required if ratingTextHref is specified). */
  trkTaxonomy: PropTypes.shape({
    /** Taxonomy page type. */
    pageType: PropTypes.string,

    /** Taxonomy topic. */
    topic: PropTypes.string,

    /** Taxonomy vertical. */
    vertical: PropTypes.string
  }),
  productType: PropTypes.string
};
BaseMarketplaceStarRating.defaultProps = {
  analytics: {},
  className: null,
  layout: LAYOUTS.INLINE,
  hideNumber: false,
  hideRatingText: false,
  ratingTextHref: null,
  omitRatingTextHref: false,
  omitRatingNumberHref: true,
  ratingTextTooltip: null,
  tooltipPlacement: 'top',
  shouldDisplayInline: false,
  showNotYetRated: false,
  smallRatingText: false,
  starRating: 0,
  trkLocation: 'marketplace-star-rating',
  trkPageType: null,
  trkTaxonomy: _get(global.window, 'nwPageTaxonomy'),
  productType: null
};
BaseMarketplaceStarRating.fragment = gql(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  fragment BaseMarketplaceStarRating on MarketplaceEntityInterface {\n    ...MarketplaceReviewLink\n  }\n  ", "\n"])), GraphQlMarketplaceReviewLink.fragment);
export default BaseMarketplaceStarRating;